import { useEffect, useState, useCallback } from "react";
import { startRegistration, startAuthentication, browserSupportsWebAuthn, platformAuthenticatorIsAvailable } from "@simplewebauthn/browser";
import { Button } from "@/components/ui/button";
import { webauthn, auth } from "@/lib/api";
import { Fingerprint, Loader2, Smartphone, Trash2 } from "lucide-react";
import { toast } from "sonner";

/* -------- Login button: appears on the Sign-in page ------------------- */
export function WebAuthnLoginButton({ username, onAuthenticated, disabled }) {
  const [supported, setSupported] = useState(false);
  const [hasPasskey, setHasPasskey] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!browserSupportsWebAuthn()) return;
      try {
        const platform = await platformAuthenticatorIsAvailable();
        if (cancelled) return;
        setSupported(platform);
      } catch (err) {
        // Older browsers / non-secure context — feature is simply unavailable
        console.warn("[WebAuthn] platformAuthenticatorIsAvailable failed", err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!supported) { setHasPasskey(false); return; }
      try {
        const { data } = await webauthn.status(username);
        if (cancelled) return;
        setHasPasskey(username ? !!data.has_passkey : !!data.any_passkey);
      } catch (err) {
        // Non-fatal — passkey badge just won't render
        console.warn("[WebAuthn] status fetch failed", err);
      }
    })();
    return () => { cancelled = true; };
  }, [username, supported]);

  const handle = useCallback(async () => {
    setBusy(true);
    try {
      const { data: options } = await webauthn.loginOptions(username);
      const assertion = await startAuthentication({ optionsJSON: options });
      const { data } = await webauthn.loginVerify(assertion);
      auth.setSession(data.token, {
        emp_no: data.emp_no,
        full_name: data.full_name,
        username: data.username,
        role: data.role,
      });
      toast.success(`Welcome, ${data.full_name}`);
      onAuthenticated?.(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Fingerprint sign-in failed";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }, [username, onAuthenticated]);

  if (!supported || !hasPasskey) return null;

  return (
    <Button
      type="button"
      variant="outline"
      onClick={handle}
      disabled={disabled || busy}
      data-testid="webauthn-login-btn"
      className="w-full border-cyan-300 text-cyan-800 hover:bg-cyan-50"
    >
      {busy ? <Loader2 size={16} className="animate-spin mr-2" /> : <Fingerprint size={16} className="mr-2" />}
      Sign in with Fingerprint / Face ID
    </Button>
  );
}

/* -------- Enrol panel: shown to logged-in users ----------------------- */
export function WebAuthnEnrolPanel() {
  const [supported, setSupported] = useState(false);
  const [devices, setDevices] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const loadDevices = useCallback(async () => {
    try {
      const { data } = await webauthn.listDevices();
      setDevices(data.devices || []);
    } catch (err) {
      // Non-fatal — likely 401 if token expired
      console.warn("[WebAuthn enrol] listDevices failed", err);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    (async () => {
      if (!browserSupportsWebAuthn()) { setLoaded(true); return; }
      try {
        const platform = await platformAuthenticatorIsAvailable();
        setSupported(platform);
      } catch (err) {
        console.warn("[WebAuthn enrol] platformAuthenticatorIsAvailable failed", err);
      }
      loadDevices();
    })();
  }, [loadDevices]);

  const enrol = async () => {
    setBusy(true);
    try {
      const label = navigator.userAgent.match(/iPhone|iPad/) ? "iPhone / iPad" :
                    navigator.userAgent.match(/Android/) ? "Android phone" :
                    navigator.userAgent.match(/Mac/) ? "Mac" :
                    navigator.userAgent.match(/Windows/) ? "Windows PC" : "This device";
      const { data: options } = await webauthn.registerOptions(label);
      const attResp = await startRegistration({ optionsJSON: options });
      await webauthn.registerVerify(attResp, label);
      toast.success("Device enrolled — you can now sign in with biometrics next time");
      await loadDevices();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Enrolment failed";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const removeDevice = async (credentialId) => {
    if (!window.confirm("Remove this device? You won't be able to sign in with its fingerprint until you re-enrol.")) return;
    try {
      await webauthn.deleteDevice(credentialId);
      toast.success("Device removed");
      await loadDevices();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove device");
    }
  };

  if (!loaded) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4 sm:p-5" data-testid="webauthn-panel">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-sm bg-cyan-50 text-cyan-700 flex items-center justify-center flex-shrink-0">
          <Fingerprint size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-slate-900">Biometric sign-in</div>
          <div className="text-xs text-slate-600 mt-0.5">
            Skip your password next time — unlock the app with your phone&apos;s fingerprint or Face ID.
          </div>
        </div>
      </div>

      {devices.length > 0 && (
        <ul className="divide-y divide-slate-100 mb-3 border border-slate-100 rounded-sm">
          {devices.map((d) => (
            <li key={d.credential_id} className="flex items-center justify-between px-3 py-2 text-xs">
              <div className="flex items-center gap-2 min-w-0">
                <Smartphone size={14} className="text-slate-500 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="font-medium text-slate-800 truncate">{d.device_label || "This device"}</div>
                  <div className="text-[10px] text-slate-500">
                    Enrolled {new Date(d.created_at).toLocaleDateString()}
                    {d.last_used_at && <> · Last used {new Date(d.last_used_at).toLocaleDateString()}</>}
                  </div>
                </div>
              </div>
              <button
                onClick={() => removeDevice(d.credential_id)}
                data-testid={`webauthn-delete-${d.credential_id.slice(0, 8)}`}
                className="text-slate-400 hover:text-red-600 transition-colors flex-shrink-0 ml-2"
                title="Remove device"
              >
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {supported ? (
        <Button
          onClick={enrol}
          disabled={busy}
          variant="outline"
          data-testid="webauthn-enrol-btn"
          className="w-full border-cyan-300 text-cyan-800 hover:bg-cyan-50"
        >
          {busy ? <Loader2 size={16} className="animate-spin mr-2" /> : <Fingerprint size={16} className="mr-2" />}
          {devices.length > 0 ? "Enrol another device" : "Enrol this device for biometric login"}
        </Button>
      ) : (
        <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-sm px-3 py-2 text-center" data-testid="webauthn-unsupported">
          Open this page on a phone with fingerprint or Face ID to enrol biometric sign-in.
        </div>
      )}
    </div>
  );
}
